import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ChevronRight, MapPin, Crown, Sparkles } from 'lucide-react';
import { PageContainer } from '../../components/layout/PageContainer/PageContainer';
import { propertyAmenities, heroImages } from '../../data/mockData';

export const PropertyInfoPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PageContainer
      heroImage={heroImages.propertyInfo}
      heroHeight="small"
      heroTitle={t('property.title')}
      heroSubtitle={t('property.subtitle')}
      overlayOpacity={0.45}
    >
      {/* Property Introduction */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gradient-to-br from-[#FDF6E9] to-[#F5E6D3] rounded-2xl p-8 mb-8"
      >
        <div className="flex items-center gap-2 mb-4">
          <Crown className="w-6 h-6 text-[#D4A054]" />
          <span className="text-[#D4A054] font-medium uppercase tracking-wider text-sm">{t('property.veniceOfEast')}</span>
        </div>
        
        <h2 className="font-display text-2xl text-[#333333] mb-4">
          {t('property.heroTitle')}
        </h2>
        
        <p className="text-[#666666] leading-relaxed mb-6">
          {t('property.heroDescription')}
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-[#D4A054] mt-0.5" />
            <div>
              <h4 className="font-medium text-[#333333]">{t('property.highlights.location.title')}</h4>
              <p className="text-sm text-[#666666]">{t('property.highlights.location.description')}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-[#D4A054] mt-0.5" />
            <div>
              <h4 className="font-medium text-[#333333]">{t('property.highlights.experiences.title')}</h4>
              <p className="text-sm text-[#666666]">{t('property.highlights.experiences.description')}</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Crown className="w-5 h-5 text-[#D4A054] mt-0.5" />
            <div>
              <h4 className="font-medium text-[#333333]">{t('property.highlights.palace.title')}</h4>
              <p className="text-sm text-[#666666]">{t('property.highlights.palace.description')}</p>
            </div>
          </div>
        </div>
        
        <div className="border-t border-[#D4A054]/20 pt-6">
          <p className="text-[#666666] italic">
            {t('property.quote')}
          </p>
        </div>
      </motion.div>

      {/* Property Amenities */}
      <div className="space-y-6">
        {propertyAmenities.map((amenity, index) => (
          <motion.div
            key={amenity.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.3 }}
            className="relative h-64 rounded-2xl overflow-hidden cursor-pointer group"
          >
            {/* Background Image */}
            <div className="absolute inset-0">
              <img
                src={amenity.image}
                alt={amenity.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>

            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-[rgba(26,26,26,0.8)] via-[rgba(26,26,26,0.4)] to-transparent" />

            {/* Content */}
            <div className="relative h-full flex flex-col justify-center p-8">
              <h3 className="font-display text-3xl text-white mb-2">
                {amenity.name}
              </h3>
              <p className="text-white/80 text-lg mb-2">
                {amenity.tagline}
              </p>
              <p className="text-white/60 text-sm max-w-md">
                {amenity.description}
              </p>

              {/* Expand Button */}
              <div className="absolute right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white group-hover:bg-white group-hover:text-[#D4A054] transition-all duration-300">
                <ChevronRight className="w-6 h-6" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </PageContainer>
  );
};

export default PropertyInfoPage;
